import { GetServerSidePropsContext } from 'next'
import React, { useEffect } from 'react'
import { QueryClient } from 'react-query'
import { dehydrate } from 'react-query/hydration'

import {
  MainLayout,
  MainLayoutContent,
  MainLayoutSidebar
} from 'src/features/layouts'
import {
  profileSpecialistApi,
  // useProfileSpecialistDetailsFields,
  useQueryProfileSpecialistById,
  useQuerySimilarSpecialist,
  useOwnerProfileFreelancer,
  useOwnerProfileWorker,
  ProfileSpecialistUserInfoNew
  // ProfileSpecialistDetails
} from 'src/features/profile-specialist/common'
import {
  portfolioApi,
  PORTFOLIO_PROJECT_LIST_KEY
  // useQueryProfilePortfolioProjectList
} from 'src/features/portfolio'
import {
  // ProfileDetailsCardRead,
  ProfileReviewForm
  // PROFILE_DETAILS_BUTTON
} from 'src/features/profiles'
import {
  profilesApi,
  PROFILES_KEY,
  PROFILE_REVIEWS_KEY,
  PROFILE_TYPE
} from 'src/features/profiles/common'
import { SERVICES_URL, theme, useTranslation } from '@plandi/common'
import { BreadCrumbs } from 'src/features/breadcrumbs/breadcrumbs'
import { SimilarSlider } from 'src/features/SimilarSlider/slider-specialists-and-compaines/similar-slider'
import { useMediaQuery } from '@material-ui/core'
import { useRouter } from 'next/router'
import { TRANSLATE, sharedApi } from 'src/shared'
import { MyProfileSpecialistNew } from 'src/features/profile-specialist'

export async function getServerSideProps({
  params,
  locale
}: GetServerSidePropsContext) {
  const queryClient = new QueryClient()

  const profileId = params?.profileId ? Number(params.profileId) : undefined

  if (profileId) {
    await Promise.all([
      queryClient.prefetchQuery(
        [PROFILES_KEY, PROFILE_TYPE.SPECIALIST, profileId, locale],
        () =>
          profileSpecialistApi.getProfileSpecialistById(
            profileId,
            locale ?? 'en'
          )
      ),
      queryClient.prefetchQuery(
        [PROFILES_KEY, PROFILE_TYPE.FREELANCER, profileId, locale],
        () => profileSpecialistApi.getMyProfileFreelancer(locale ?? 'en')
      ),
      queryClient.prefetchQuery(
        [PROFILES_KEY, PROFILE_TYPE.WORKER, profileId, locale],
        () => profileSpecialistApi.getMyProfileWorker(locale ?? 'en')
      ),
      queryClient.prefetchQuery(
        [PORTFOLIO_PROJECT_LIST_KEY, profileId, {}, locale],
        () =>
          portfolioApi.getProfilePortfolioProjectList(locale ?? 'en', profileId)
      ),
      queryClient.prefetchQuery([PROFILE_REVIEWS_KEY, profileId, locale], () =>
        profilesApi.getProfileReviews(profileId, locale ?? 'en')
      ),
      queryClient.prefetchQuery(TRANSLATE, () =>
        sharedApi.getTranslate(locale ?? 'en')
      )
    ])
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      locale: locale
    }
  }
}

const ResumeProfilePage = (props: { locale: string }) => {
  const router = useRouter()
  const { data: translate } = useTranslation()

  const isOwnerProfileFreelancer = useOwnerProfileFreelancer()
  const isOwnerProfileWorker = useOwnerProfileWorker()
  // const detailsFields = useProfileSpecialistDetailsFields()
  const isDesktop = useMediaQuery(theme.breakpoints.up(960))

  const isOwnerProfile = isOwnerProfileFreelancer || isOwnerProfileWorker

  const profileId = router.query.profileId
    ? Number(router.query.profileId)
    : undefined

  const { data: resume, isLoading } = useQueryProfileSpecialistById()

  // const { data: projects } = useQueryProfilePortfolioProjectList()

  const {
    data: similarSpecialist,
    refetch,
    isFetching
  } = useQuerySimilarSpecialist(profileId)

  useEffect(() => {
    refetch()
  }, [profileId, refetch])

  const url = SERVICES_URL
  const locale = props.locale

  // const isVisiblePortfolioButton =
  //   (projects && projects.length > 0) || isOwnerProfile

  const profileType = isOwnerProfileFreelancer
    ? PROFILE_TYPE.FREELANCER
    : isOwnerProfileWorker
    ? PROFILE_TYPE.WORKER
    : PROFILE_TYPE.SPECIALIST

  // const buttonVariants = isVisiblePortfolioButton
  //   ? isOwnerProfile
  //     ? [PROFILE_DETAILS_BUTTON.EDIT, PROFILE_DETAILS_BUTTON.PORTFOLIO]
  //     : projects?.length
  //     ? [PROFILE_DETAILS_BUTTON.MESSAGE, PROFILE_DETAILS_BUTTON.PORTFOLIO]
  //     : [PROFILE_DETAILS_BUTTON.MESSAGE]
  //   : [PROFILE_DETAILS_BUTTON.MESSAGE]

  return (
    <MainLayout
      withFooter
      title={resume?.fullName}
      description={resume?.aboutMe}
    >
      <div style={{ width: '100%' }}>
        <BreadCrumbs
          breadcrumbInfo={[
            {
              title: translate?.['job']?.['title2'] ?? 'Фриланс и Работа',
              href:
                locale === 'ru'
                  ? `${url.job}/ru/factories`
                  : `${url.job}/factories`
            },
            {
              title: translate?.specialists ?? 'Специалисты',
              href:
                locale === 'ru'
                  ? `${url.job}/ru/employers`
                  : `${url.job}/employers`
            },
            {
              title: `${resume?.fullName}`,
              href: ''
            }
          ]}
        />
        <div style={isDesktop ? { display: 'flex' } : {}}>
          <MainLayoutSidebar withSticky size="large">
            <ProfileSpecialistUserInfoNew
              type={profileType}
              translate={translate}
              profile={resume}
              hideDeactivation
              absoluteRating
              withChatButton
            />
            {!isOwnerProfile && (
              <ProfileReviewForm
                reviewType="company"
                isReviewed={resume?.isReviewed}
                profileId={resume?.id}
                translate={translate}
                locale={props.locale}
              />
            )}
          </MainLayoutSidebar>
          <MainLayoutContent>
            {/* <ProfileDetailsCardRead details={detailsFields} /> */}
            <MyProfileSpecialistNew
              profile={resume}
              isLoading={isLoading}
              translate={translate}
              locale={props.locale}
              type={resume?.typeSpecialist ?? 'freelancer'}
              hideEdit
            />
          </MainLayoutContent>
        </div>

        {similarSpecialist?.length && !isFetching && (
          <SimilarSlider
            title={
              translate?.['job-site']?.['similar-specialists'] ??
              'Похожие специалисты'
            }
            sliderData={similarSpecialist}
            translate={translate}
            locale={props.locale}
          />
        )}
      </div>
    </MainLayout>
  )
}

export default ResumeProfilePage
